import { useEffect, useRef, useState } from "react";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import { ILanguage, IPrivacyNotice } from "../../../models/Interfaces";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import AppModal from "../../components/common/AppModal";
import { Col, Form, FormControl, FormGroup, FormLabel, FormSelect, Row, Tab, TabPane, Tabs } from "react-bootstrap";
import HTMLEditor from "../../components/common/HTMLEditor";
import PageSelector from "../../components/common/PageSelector";
import FormCheckInput from "react-bootstrap/esm/FormCheckInput";
import ColourPicker from "../../components/common/ColourPicker";
import { Core, formValidation, notEmpty } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { Excluded } from '@form-validation/plugin-excluded';

interface IAddUpdatePrivacyNoticeModal {
  notice: IPrivacyNotice;
  onClose: () => void;
  onSave: (notice: IPrivacyNotice) => void;
  languages: Array<ILanguage>;
}

const NoticeTabs = {
  Content: "content",
  Styling: "styling"
};

const NoticePosition = [
  { id: "top", position: "Top" },
  { id: "bottom", position: "Bottom" }
];
 

type FormControlElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

const AddUpdatePrivacyNoticeModal = (props: IAddUpdatePrivacyNoticeModal) => {
  const [ selectedNotice, setSelectedNotice ] = useState<IPrivacyNotice | null>(null);
  const [activeTab, setActiveTab] = useState<string>(NoticeTabs.Content);

  const formRef = useRef<HTMLFormElement | null>(null);
  const fvRef = useRef<Core | null>(null);
  
  const saveNotice = async () : Promise<void> => {
    if (fvRef.current && selectedNotice) {
      const validres = await fvRef.current.validate();

      if (validres === "Valid") {
        if (props.onSave) {
          props.onSave(selectedNotice);
        }
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<FormControlElement>) : void => {
    const { value, name } = event.target;

    if (name) {
      setSelectedNotice(prev => ({
        ...prev as IPrivacyNotice,
        [name]: value
      }));
    }
  };

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>) : void => {
    const { name, checked } = event.target;

    if (name) {
      setSelectedNotice(prev => ({
        ...prev as IPrivacyNotice,
        [name]: checked
      }));
    }
  };

  const createValidation = () : void => {
    if (formRef.current && !fvRef.current) {
      fvRef.current = createFormValidation(formRef.current, {
        fields: {
          Language: {
            validators: {
              notEmpty: { message: "Please select the language for the notice" }
            }
          },
          AgreeButtonText: {
            validators: {
              notEmpty: { message: "Agree button text is required" }
            }
          },
          DisplayText: {
            validators: {
              notEmpty: { message: "Content to display is required" }
            }
          },
          PolicyUrl: {
            validators: {
              notEmpty: { message: "A link to your policy page is required" }
            }
          },
          PolicyUrlLinkText: {
            validators: {
              notEmpty: { message: "A description for the link is required" } 
            }
          },
          Location: {
            validators: {
              notEmpty: { message: "Please select where you want the message to be displayed" }
            }
          }
        },
        plugins: {
          bootstrap5: new Bootstrap5(),
          trigger: new Trigger(),
          excluded: new Excluded(),          
        }        
      })
    }
  };

  useEffect(() => {
    setSelectedNotice(props.notice);
  }, [props.notice]);

  useEffect(() => {
    if (formRef.current) {
      createValidation();
    }
  }, []);

  return (
    <AppModal
      onClose={props.onClose}
      onDataAction={saveNotice}
      size="xl"
      showButtons={true}
      dataActionButtonText="Save Notice"
      title="Privacy Notice Details"
      actionButtonIcon={faSave}
      actionButtonVariant="success"
      cancelButtonVariant="primary">
        <Form id="NoticeForm" ref={formRef}>
          <Tabs activeKey={activeTab}
            onSelect={(tk) => setActiveTab(tk ?? NoticeTabs.Content)}>
            <Tab eventKey={NoticeTabs.Content} title="Content">
              <TabPane active={activeTab === NoticeTabs.Content}>
                <Row>
                  <Col md={12} xl={6} className="mt-3">
                    <FormLabel htmlFor="Language">Language</FormLabel>
                    <FormSelect
                      id="Language"
                      name="Language"
                      title="Select site language"
                      value={selectedNotice?.Language ?? ""}
                      onChange={handleInputChange}>
                        <option value="">Select site language...</option>
                        {
                          props.languages.map(lang => (
                            <option key={`LANG-${lang.ShortCode}`} value={lang.ShortCode}>{lang.Language}</option>
                          ))
                        }
                    </FormSelect>
                  </Col>
                  <Col md={12} xl={6} className="mt-3">
                    <FormLabel htmlFor="PolicyUrl">Select privacy page</FormLabel>
                    <PageSelector
                      id="PolicyUrl"
                      name="PolicyUrl"
                      onInputSelect={handleInputChange}
                      existingUrl={selectedNotice?.PolicyUrl ?? ""}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12} xl={6} className="mt-3">
                    <FormLabel htmlFor="PolicyUrlLinkText">Policy Link Text</FormLabel>
                    <FormControl
                      type="text"
                      id="PolicyUrlLinkText"
                      name="PolicyUrlLinkText"
                      value={selectedNotice?.PolicyUrlLinkText ?? ""}
                      onChange={handleInputChange}
                      placeholder="e.g. our privacy policy"
                      autoComplete="off"
                    />
                  </Col>
                  <Col md={12} xl={6} className="mt-3">
                    <FormLabel htmlFor="AgreeButtonText">Agree Button Text</FormLabel>
                    <FormControl
                      type="text"
                      id="AgreeButtonText"
                      name="AgreeButtonText"
                      value={selectedNotice?.AgreeButtonText ?? ""}
                      onChange={handleInputChange}
                      placeholder="e.g. I agree"
                      autoComplete="off"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12} className="mt-3">
                    <FormLabel htmlFor="DisplayText">Policy Content</FormLabel>
                    <HTMLEditor documentKey={selectedNotice?.Id}
                      id="DisplayText"
                      name="DisplayText"
                      value={selectedNotice?.DisplayText ?? ""}
                      onInputChange={handleInputChange}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12} xl={4} className="mt-3">
                    <FormLabel htmlFor="Enabled">Enabled</FormLabel>
                    <FormCheckInput 
                      className="form-check"
                      name="Enabled"
                      id="Enabled"
                      checked={selectedNotice?.Enabled ?? false}
                      onChange={handleCheckChange}
                      title="enable or disable the policy notice"
                    />
                  </Col>
                </Row>
              </TabPane>
            </Tab>
            <Tab eventKey={NoticeTabs.Styling} title="Styling">
              <TabPane active={activeTab === NoticeTabs.Styling}>
                <Row>
                  <Col md={12} xl={6} className="mt-3">
                    <FormLabel htmlFor="ContainerClass">Container Class (bootstrap or custom css class)</FormLabel>
                    <FormControl
                      type="text"
                      id="ContainerClass"
                      name="ContainerClass"
                      value={selectedNotice?.ContainerClass ?? ""}
                      onChange={handleInputChange}
                      autoComplete="off"
                    />
                  </Col>
                  <Col md={12} xl={6} className="mt-3">
                    <FormLabel htmlFor="ButtonClass">Button Class (bootstrap or custom css class)</FormLabel>
                    <FormControl
                      type="text"
                      id="ButtonClass"
                      name="ButtonClass"
                      value={selectedNotice?.ButtonClass ?? ""}
                      onChange={handleInputChange}
                      autoComplete="off"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12} xl={6} className="mt-3">
                    <FormLabel htmlFor="BackgroundColour">Background Colour</FormLabel>
                    <ColourPicker
                      id="BackgroundColour"
                      name="BackgroundColour"
                      selectedColour={selectedNotice?.BackgroundColour ?? ""}
                      onInputChange={handleInputChange}
                    />
                  </Col>
                  <Col md={12} xl={6} className="mt-3">
                    <FormLabel htmlFor="TextColour">Text Colour</FormLabel>
                    <ColourPicker
                      id="TextColour"
                      name="TextColour"
                      selectedColour={selectedNotice?.TextColour ?? ""}
                      onInputChange={handleInputChange}
                    />
                  </Col>                  
                </Row>
                <Row>
                  <Col md={12} xl={4} className="mt-3">
                    <FormLabel htmlFor="Location">Location</FormLabel>
                    <FormSelect
                      id="Location"
                      name="Location"
                      value={selectedNotice?.Location ?? ""}
                      title="select notice position"
                      onChange={handleInputChange}>
                        <option value="">Select notice position...</option>
                        {
                          NoticePosition.map(np => (
                            <option key={`NP-${np.id}`} value={np.id}>{np.position}</option>
                          ))
                        }
                    </FormSelect>
                  </Col>
                </Row>
              </TabPane>              
            </Tab>
          </Tabs>
        </Form>
    </AppModal>
  );
}

export default AddUpdatePrivacyNoticeModal;
